import React from "react";

// Shape of the question form the parent (QuizManagement) holds in state.
export type QuestionKindT = "single" | "multi" | "true_false";
export interface QuestionFormValue {
  text: string;
  media_type: "text" | "image" | "video";
  media_url: string;
  video_duration: number;
  kind: QuestionKindT;
  options: string[];
  correct_answer: number | number[];
  explanation: string;
  points: number;
  time_limit_seconds: number | "";
  chapter_index: number | null;
}

// Default point value suggested per answer type. Auto-filled when the type
// changes so an admin rarely has to touch the Puncte field.
export const defaultPointsForKind = (kind: QuestionKindT): number =>
  kind === "true_false" ? 5 : kind === "multi" ? 15 : 10;

interface Props {
  mode: "add" | "edit";
  form: QuestionFormValue;
  setForm: React.Dispatch<React.SetStateAction<QuestionFormValue>>;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  videoChapters?: { title: string }[] | null;
}

const KIND_TABS: { key: QuestionKindT; label: string }[] = [
  { key: "single", label: "Alegere unică" },
  { key: "multi", label: "Alegere multiplă" },
  { key: "true_false", label: "Adevărat / Fals" },
];

export default function QuestionEditorModal({ mode, form, setForm, onSubmit, onClose, videoChapters }: Props) {
  const isMulti = form.kind === "multi";

  const changeKind = (newKind: QuestionKindT) => {
    setForm((prev) => {
      const base: QuestionFormValue = { ...prev, kind: newKind, points: defaultPointsForKind(newKind) };
      if (newKind === "true_false") {
        return { ...base, options: ["True", "False"], correct_answer: 0 };
      }
      // Coming FROM true_false → start fresh option slots; otherwise keep them.
      const options = prev.kind === "true_false" ? ["", "", "", ""] : prev.options;
      return { ...base, options, correct_answer: newKind === "multi" ? [] : 0 };
    });
  };

  const isCorrect = (index: number) =>
    (isMulti && Array.isArray(form.correct_answer) && form.correct_answer.includes(index)) ||
    (!isMulti && form.correct_answer === index);

  const toggleCorrect = (index: number) => {
    setForm((prev) => {
      if (prev.kind === "multi") {
        const cur = Array.isArray(prev.correct_answer) ? prev.correct_answer : [];
        return { ...prev, correct_answer: cur.includes(index) ? cur.filter((i) => i !== index) : [...cur, index] };
      }
      return { ...prev, correct_answer: index };
    });
  };

  const inputCls =
    "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col border border-slate-200 animate-modal-in overflow-hidden">
        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-white">
          <h2 className="text-lg font-bold text-slate-800">
            {mode === "add" ? "Adaugă întrebare" : "Editează întrebare"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable body */}
          <div className="px-5 py-4 space-y-4 overflow-y-auto">
            {/* Question text */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Text întrebare *</label>
              <textarea
                required
                rows={2}
                value={form.text}
                onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
                placeholder="Scrie întrebarea..."
                className={inputCls}
              />
            </div>

            {/* Answer type — segmented */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Tip răspuns</label>
              <div className="grid grid-cols-3 gap-1.5">
                {KIND_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => changeKind(tab.key)}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold transition ${
                      form.kind === tab.key
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Options / correct answer */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                {form.kind === "true_false"
                  ? "Răspunsul corect"
                  : isMulti
                    ? "Opțiuni — bifează toate corecte"
                    : "Opțiuni — bifează corectă"}
              </label>

              {form.kind === "true_false" ? (
                <div className="flex gap-2">
                  {["Adevărat", "Fals"].map((label, index) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, correct_answer: index }))}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition ${
                        form.correct_answer === index
                          ? index === 0
                            ? "bg-emerald-500 text-white shadow-sm"
                            : "bg-red-500 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {form.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => toggleCorrect(index)}
                        title={isCorrect(index) ? "Corect" : "Marchează corect"}
                        className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition ${
                          isCorrect(index) ? "bg-emerald-500 text-white shadow-sm" : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                        }`}
                      >
                        ✓
                      </button>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const opts = [...form.options];
                          opts[index] = e.target.value;
                          setForm((p) => ({ ...p, options: opts }));
                        }}
                        placeholder={`Opțiunea ${index + 1}`}
                        className={`flex-1 ${inputCls}`}
                      />
                      {form.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const opts = form.options.filter((_, i) => i !== index);
                            let correct = form.correct_answer;
                            if (isMulti && Array.isArray(correct)) {
                              correct = correct.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i));
                            } else if (typeof correct === "number" && correct >= index) {
                              correct = Math.max(0, correct - 1);
                            }
                            setForm((p) => ({ ...p, options: opts, correct_answer: correct }));
                          }}
                          className="shrink-0 w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, options: [...p.options, ""] }))}
                    className="mt-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition font-semibold text-xs"
                  >
                    + Adaugă opțiune
                  </button>
                </div>
              )}
            </div>

            {/* Points + time, side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Puncte</label>
                <input
                  type="number"
                  min={1}
                  value={form.points}
                  onChange={(e) => setForm((p) => ({ ...p, points: parseInt(e.target.value) || 1 }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Timp răspuns <span className="text-slate-400 normal-case font-normal">(sec)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.time_limit_seconds}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, time_limit_seconds: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0) }))
                  }
                  placeholder="Ex: 20"
                  className={inputCls}
                />
              </div>
            </div>

            {/* Explanation */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Explicație <span className="text-slate-400 normal-case font-normal">(opțional — apare după răspuns)</span>
              </label>
              <textarea
                value={form.explanation}
                onChange={(e) => setForm((p) => ({ ...p, explanation: e.target.value }))}
                rows={2}
                placeholder="De ce este corect răspunsul..."
                className={inputCls}
              />
            </div>

            {/* Chapter (only for chaptered video quizzes) */}
            {videoChapters && videoChapters.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Capitol video</label>
                <select
                  value={form.chapter_index !== null ? form.chapter_index : ""}
                  onChange={(e) => setForm((p) => ({ ...p, chapter_index: e.target.value === "" ? null : parseInt(e.target.value) }))}
                  className={inputCls}
                >
                  <option value="">Fără capitol</option>
                  {videoChapters.map((chapter, index) => (
                    <option key={index} value={index}>{chapter.title}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Sticky footer — distinct background so it reads as an action bar */}
          <div className="flex gap-2 justify-end px-5 py-3 border-t border-slate-200 bg-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-semibold text-sm"
            >
              Anulează
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-semibold text-sm shadow-sm"
            >
              {mode === "add" ? "Adaugă întrebare" : "Salvează"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
